
import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { useNavigate } from 'react-router-dom';
import { setUserContext, clearUserContext } from '@/lib/sentry';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Listen for auth changes FIRST (before getSession to avoid race conditions)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          setUserContext({ id: session.user.id, email: session.user.email });
        }
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        setLoading(false);
        clearUserContext();
        navigate('/auth');
      } else if (event === 'INITIAL_SESSION') {
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          setUserContext({ id: session.user.id, email: session.user.email });
        }
      }
    });

    // Get initial session as fallback
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        setUserContext({ id: session.user.id, email: session.user.email });
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const signOut = async () => {
    try {
      // Clear user state immediately for instant UI feedback
      setUser(null);
      clearUserContext();
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
        // Force navigation even if signOut API fails
      }
      navigate('/auth');
    } catch (error) {
      console.error('Error during sign out:', error);
      // Force navigation even on error
      navigate('/auth');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
